<template>
	<div class="find-location container-fluid">
		<div class="d-flex justify-content-between py-2">
			<h1 class="location-heading h6 mb-0">Find a Location</h1>
			<a v-if="showComingSoon" class="d-none d-lg-inline" :href="comingSoonClubsUrl">
				Coming Soon Clubs
			</a>
		</div>
		<div
			v-if="isLoading"
			class="d-flex justify-content-center align-items-center"
			style="height: 90vh"
		>
			<div class="spinner-border" role="status">
				<span class="visually-hidden">Loading...</span>
			</div>
		</div>

		<div v-else class="row">
			<div class="col-lg-7">
				<div class="results-wrapper">
					<LocationSearch
						:matching-locations="matchingLocations"
						@set-search-position="handleSetSearchPosition"
					/>

					<LocationList
						:matching-locations="matchingLocations"
						:selected-location="selectedLocation"
						:is-mobile-map-view="isMobileMapView.valueOf()"
						@card-hover-change="handleCardHoverChange"
						@card-click="handleCardClick"
					/>
				</div>
			</div>

			<div class="col-lg-5 position-relative">
				<LocationMap
					:map-center="mapCenter"
					:all-locations="locations"
					:matching-locations="matchingLocations"
					:hovered-location="hoveredLocation"
					:selected-location="selectedLocation"
					@marker-click="handleMarkerClick"
					@info-window-close="selectedLocation = null"
					@map-bounds-set="bounds = $event || null"
				/>
			</div>
		</div>

		<div class="container-fluid fixed-bottom text-center pb-5 d-lg-none">
			<button
				id="mapToggle"
				type="button"
				class="btn btn-blur-dark btn-sm px-3"
				data-testid="map-toggle"
				@click="handleMapToggleClick"
			>
				{{ isMobileMapView ? 'List' : 'Map' }}
			</button>
		</div>

		<div class="pt-3">
			<p
				v-if="showSurchargeDisclaimer"
				id="surcharge-disclaimer"
				data-testid="surcharge-disclaimer"
				class="small"
			>
				&ast; Surcharge laws apply.
			</p>
			<p id="terms-disclaimer" class="small">
				Membership levels, types, amenities, services, programs and their associated dues, pricing,
				fees and/or charges may vary by location and are subject to change. Access to clubs, and
				certain services, programs, amenities, or areas within a club (e.g., outdoor pools, racquet
				courts) may be restricted, conditioned, or otherwise limited by specific terms and
				conditions and/or subject to a separate or supplemental fee for access or use (e.g., your
				access to the outdoor pool area may require a separate fee and be limited to your designated
				club). See club for additional terms and conditions that may apply. Always consult your
				physician before beginning any new exercise program.
			</p>
		</div>
	</div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, ref, watch } from 'vue';
import { efcConstants, sessionService } from '@life-time-inc/enterprise-framework-core';

import LocationSearch from 'Lib/components/find-a-location/LocationSearch.vue';
import LocationList from 'Lib/components/find-a-location/LocationList.vue';
import LocationMap from 'Lib/components/find-a-location/LocationMap.vue';
import { milesBetween } from 'Lib/components/find-a-location/util/miles-between';
import { defaultCenter } from 'Lib/components/find-a-location/util/mapConfig';

import { aemServletService } from '@/services/aemServletService';
import { useMapStore } from '@/stores/map';
import { useD360MapFiltersStore } from '@/stores/map-d360-filters';
import { getGlobalPageDriverUrl } from '@/util/globalPageDriver';
import { logError, logWarn } from '@/util/logger';
import { prerenderGateCheck } from '@/util/prerenderGateCheck';

import type { Location } from 'Types/aemui/find-a-location/findaLocation';

const comingSoonClubsUrl = ref('');
const mapStore = useMapStore();
const { experienceType, showComingSoon } = storeToRefs(mapStore);

/** all selected filters (read from the URL) */
const d360MapStore = useD360MapFiltersStore();
const { hasFilters, selectedFilters } = storeToRefs(d360MapStore);

/** all locations */
const locations = ref<Location[]>([]);
/** whether initial load of the UI is happening */
const isLoading = ref<boolean>(true);
/** the geoPosition of the user (if they allow it), or null */
const geoPosition = ref<{ lat: number; lng: number } | null>(null);

onBeforeMount(async () => {
	if (prerenderGateCheck()) {
		return;
	}

	mapStore.setMapState();

	comingSoonClubsUrl.value = getGlobalPageDriverUrl('comingSoonClubs');
	storeSsoId();

	const supportsGeolocation = 'navigator' in window && 'geolocation' in navigator;
	if (supportsGeolocation) {
		window.navigator.geolocation.getCurrentPosition(
			(position) => {
				geoPosition.value = {
					lat: position.coords.latitude,
					lng: position.coords.longitude
				};
			},
			(error: GeolocationPositionError) => {
				if (error.code === 1) {
					return;
				}
				logWarn('Geolocation error', { errorObject: error.message });
			}
		);
	}

	locations.value = await loadLocations();
	isLoading.value = false;
});

/**
 * Checks for an SSOID passed in the URL.
 * If found, stores it in a cookie and removes it from the URL
 */
function storeSsoId() {
	const ssoIdQueryParamName = 'SSOID';

	const url = new URL(window.location.href);
	const ssoId = url.searchParams.get(ssoIdQueryParamName);

	if (ssoId) {
		const { CookieConstant } = efcConstants;
		sessionService.setCookie(CookieConstant.ssoId, ssoId, CookieConstant.ssoIdExpires);
		url.searchParams.delete(ssoIdQueryParamName);
		window.history.replaceState(null, '', url);
	}
}

async function loadLocations() {
	try {
		if (experienceType.value === 'lifeclinic') {
			const response = await aemServletService.getLifeClinicLocations();
			return response.data;
		} else if (experienceType.value === 'prospect') {
			const response = await aemServletService.getNearestClubs();
			return response.data;
		} else if (experienceType.value === 'work') {
			const response = await aemServletService.getWorkLocations();
			return response.data;
		} else {
			throw new Error(
				`experienceType "${experienceType.value}" is not equal to 'prospect', 'lifeclinic', or 'work'`
			);
		}
	} catch (e: unknown) {
		logError('GET Locations Error', { experienceType: experienceType.value }, e);
		return [];
	}
}

const bounds = ref<google.maps.LatLngBounds | null>(null); // google map LatLngBounds object indicating bounds of the map

const selectedLocation = ref<Location | null>(null); // the location the user has selected

const hoveredLocation = ref<Location | null>(null); // the location the user is hovering over (on the location list)

const isMobileMapView = ref<boolean>(false); // whether to show list vs. map in the mobile view

// the user's searchText translated into GPS coords. The map centers on these to display matchingLocations
const searchPosition = ref<google.maps.LatLngLiteral | null>(null);

// sets the searchPosition and handles resetting UI
function handleSetSearchPosition(position: google.maps.LatLngLiteral) {
	searchPosition.value = position;

	// Reset the page to be scrolled to the top
	const searchEl = document.querySelector('.results-wrapper') as HTMLElement;
	const offsetTop = searchEl?.offsetTop;
	if (offsetTop && window.scrollY > offsetTop) {
		window.scrollTo({ top: offsetTop, behavior: 'smooth' });
	}

	// Clear out the selectedLocation
	if (selectedLocation.value) {
		selectedLocation.value = null;
	}
}

// the position the map gets centered on when initially rendered or after it has been moved by geolocation or user
// search. It's one of these in priority order: searchLocation, geolocation, default
const mapCenter = computed(() => {
	return searchPosition.value || geoPosition.value || defaultCenter;
});

// Returns the Life Time locations that:
//   - match selected filters
//   - are within the map bounds
//   - sorts them by ascending distance to map center
const matchingLocations = computed((): Location[] => {
	if (!mapCenter.value?.lat || !locations?.value?.length) return [];

	return locations.value
		.filter((location: Location) => {
			// if no filters, move on
			if (!hasFilters.value) return true;

			return selectedFilters.value.every((selectedFilter) => {
				return location.d360Index?.[selectedFilter.id];
			});
		})
		.map((location: Location) => ({
			...location,
			distance: Number(
				milesBetween(mapCenter.value, {
					lat: Number(location.latitude),
					lng: Number(location.longitude)
				}).toFixed(1)
			)
		}))
		.filter(
			// ensure it's within map bounds or within 50 miles if there are no
			// map bounds (e.g. mobile list view)
			(location: Location) => {
				if (bounds.value) {
					return bounds.value.contains({
						lat: Number(location.latitude),
						lng: Number(location.longitude)
					});
				}
				return typeof location.distance === 'number' && location.distance < 50;
			}
		)
		.filter((location: Location) => location.approvedForWeb === true)
		.sort((a: { distance: number }, b: { distance: number }) => a.distance - b.distance);
});

const showSurchargeDisclaimer = computed(() => {
	return matchingLocations.value.some((location: Location) => location.isSurchargeIncluded);
});

// clear the selected location if that location isn't on the map anymore (due to filtering or map movement)
watch(matchingLocations, () => {
	const selectedClubId = selectedLocation.value?.mmsClubId;
	const selectedLocationIsGone =
		selectedClubId &&
		!matchingLocations.value.some((location) => location.mmsClubId === selectedClubId);

	if (selectedLocationIsGone) {
		selectedLocation.value = null;
	}
});

// Gets relevant scroll info
function getScrollInfo() {
	const fullPageHeight = document.documentElement.scrollHeight;
	const browserViewportEl = document.querySelector('html') as HTMLElement;
	const browserViewportHeight = browserViewportEl?.clientHeight;

	const footer = document.querySelector('#terms-disclaimer') as HTMLElement;
	const footerHeight = fullPageHeight - footer?.offsetTop;

	const biggestScrollTop = fullPageHeight - browserViewportHeight - footerHeight;

	const searchEl = document.querySelector('.results-wrapper') as HTMLElement;
	const smallestScrollTop = searchEl?.offsetTop;

	return {
		smallestScrollTop,
		biggestScrollTop,
		browserViewportHeight
	};
}

function scrollToSelectedLocation() {
	if (!selectedLocation.value) {
		return;
	}
	// - Don't scroll up to put the selected location underneath sticky search area
	// - Don't scroll down into the terms-disclaimer and footer
	const { smallestScrollTop, biggestScrollTop, browserViewportHeight } = getScrollInfo();

	const el: HTMLElement | null = document.getElementById(
		`location-card-${selectedLocation.value.mmsClubId}`
	);
	let top = (el?.offsetTop || 0) - browserViewportHeight * 0.4; // 0.4 puts it around middle of screen

	if (top < smallestScrollTop) top = smallestScrollTop;
	if (top > biggestScrollTop) top = biggestScrollTop;

	window.scrollTo({ top, behavior: 'smooth' });
}

function handleMarkerClick(location: Location) {
	selectedLocation.value = location;

	const el: HTMLElement | null = document.getElementById(`location-card-${location.mmsClubId}`);

	if (isMobileMapView.value) {
		el?.scrollIntoView({ behavior: 'smooth' });
	} else if (el?.offsetTop) {
		scrollToSelectedLocation();
	}
}

function handleCardClick(location: Location | null) {
	selectedLocation.value = location;
}

function handleCardHoverChange(location: Location | null) {
	hoveredLocation.value = location;
}

function handleMapToggleClick() {
	// This manipulates the DOM directly rather than using Vue because it's
	// manipulating the body element (outside this Vue instance)
	if (isMobileMapView.value) {
		document.body?.classList.remove('find-location-mapview');
	} else {
		document.body?.classList.add('find-location-mapview');
	}
	isMobileMapView.value = !isMobileMapView.value;
}

defineExpose({
	bounds,
	geoPosition,
	hoveredLocation,
	isLoading,
	isMobileMapView,
	locations,
	matchingLocations,
	searchPosition,
	selectedFilters,
	selectedLocation,

	handleCardClick,
	handleCardHoverChange,
	handleMarkerClick,
	handleSetSearchPosition,
	scrollToSelectedLocation
});
</script>

<style>
/* Override infowindow styles */
.gm-style .gm-style-iw-d::-webkit-scrollbar {
	width: 12px !important;
}
</style>
